import Button from '@/components/elements/forms/buttons/Button/Button';
import LegacyServiceCard from '@/components/elements/Card/ServiceListCard/LegacyServiceCard';
import useMobileView from '@/hooks/useMobileView';
import { _t } from '@/locale';
import { useState } from 'react';
import { SelectServiceItem } from '../views/SelectService.types';
import InfoDrawer from './InfoDrawer';
import LegacyServiceInfoCard from './LegacyServiceInfoCard';

const bookAppointmentServiceItemKey = 'components.modules.modals.BookAppointment.shared.ServiceItem';
const baseKey = 'features.bookingSelection.ServiceItem';

const ServiceInfoDrawer = ({
  isOpen,
  onClose,
  service,
  ctaLabel,
  onPick,
}: {
  isOpen: boolean;
  onClose: () => void;
  service: SelectServiceItem;
  ctaLabel: string;
  onPick: () => void;
}) => (
  <InfoDrawer
    isOpen={isOpen}
    onClose={onClose}
    title={_t(`${baseKey}.infoTitle`)}
    footer={
      <Button block variant="primary" size="md" onClick={onPick}>
        {ctaLabel}
      </Button>
    }>
    <LegacyServiceInfoCard service={service} />
  </InfoDrawer>
);

export type LegacyServiceItemProps = {
  service: SelectServiceItem;
  onPickService: (serviceId: number, isRecommended: boolean) => void;
  onShowServiceInfo?: (serviceId: number) => void;
  /** Takes over the (i): the row opens no sheet of its own and hands the id to the caller. */
  onOpenServiceInfo?: (serviceId: number) => void;
  /** Called whenever the row's own info drawer closes — the X and the pick path both close it. */
  onHideServiceInfo?: (serviceId: number) => void;
  ctaVariant?: 'primary' | 'secondary';
  /** Overrides the CTA wording; defaults to the isRecommended rule (add vs book). */
  ctaAction?: 'book' | 'add';
};

/** Service row via the shared ServiceCard; owns only the "Om tjänsten" drawer's local state. */
const LegacyServiceItem = ({
  service,
  onPickService,
  onShowServiceInfo,
  onOpenServiceInfo,
  onHideServiceInfo,
  ctaVariant = 'primary',
  ctaAction,
}: LegacyServiceItemProps) => {
  const { isMobileView } = useMobileView();
  const [descOpen, setDescOpen] = useState(false);
  const { isRecommended, ...cardData } = service;
  const pick = () => onPickService(service.id, isRecommended);
  const resolvedCtaAction = ctaAction ?? (isRecommended ? 'add' : 'book');
  const ctaLabel = _t(`${bookAppointmentServiceItemKey}.cta.label.${resolvedCtaAction}`);

  const showInfo = () => {
    onShowServiceInfo?.(service.id);
    if (onOpenServiceInfo) onOpenServiceInfo(service.id);
    else setDescOpen(true);
  };

  return (
    <>
      <li>
        <LegacyServiceCard
          {...cardData}
          onRowClick={pick}
          onShowReadMore={service.description ? showInfo : undefined}
          alignPriceColumn
          hidePriceLabel
          rightSlot={
            <Button
              variant={ctaVariant}
              size={isMobileView ? 'md' : 'sm'}
              label={ctaLabel}
              onClick={(e) => {
                // Keep the click from bubbling to onRowClick — one click, one pick.
                e.stopPropagation();
                pick();
              }}
            />
          }
        />
      </li>
      {service.description && !onOpenServiceInfo && (
        <ServiceInfoDrawer
          isOpen={descOpen}
          onClose={() => {
            setDescOpen(false);
            onHideServiceInfo?.(service.id);
          }}
          service={service}
          ctaLabel={_t(`${baseKey}.infoCta.${resolvedCtaAction}`)}
          onPick={() => {
            setDescOpen(false);
            // Pick first, then hide — the modal arm's order (onPickService, then the close
            // that fires viewServiceDetails('cancel')).
            pick();
            onHideServiceInfo?.(service.id);
          }}
        />
      )}
    </>
  );
};

export default LegacyServiceItem;
